import axios from "axios";
import React, { useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import CustomInput from "src/app/components/CustomInput/CustomInput";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import Modal, { ModalTitle } from "src/app/components/Modal/Modal";
import { translateMessage } from "src/app/methods/translateMessage";
import { RootState } from "src/redux/reducers";
import { API_URLS } from "src/utils/API_URLS";
import { showErrorToast } from "src/utils/methods";
import { SocialInsightsDashboardListElement } from "src/redux/insights-dashboard/utils";
import {
  getDashboardList,
  setSelectedDashboard,
} from "src/redux/insights-dashboard/insightsDashboardActions";
import { showToast } from "src/app/methods/showToast";

import "./RenameDashboardModal.scss";

interface Props {
  data: SocialInsightsDashboardListElement;
  onClose: () => void;
}

export const RenameDashboardModal = (props: Props) => {
  const { data, onClose } = props;

  const [name, setName] = useState(data.name);
  const [loading, setLoading] = useState(false);

  const { activeWorkspaceUuid } = useSelector(
    (state: RootState) => state.mainReducer,
  );
  const { selectedDashboard } = useSelector(
    (state: RootState) => state.insightsDashboardReducer,
  );

  const dispatch = useDispatch();
  const intl = useIntl();
  const isNameTooLong = name.length > 128;

  const submit = async () => {
    try {
      setLoading(true);

      const newDashboard = {
        ...data,
        name,
      };

      await axios.post(API_URLS.editInsightsDashboard, {
        ...newDashboard,
        wsWorkspaceUuid: activeWorkspaceUuid,
      });

      dispatch(getDashboardList(activeWorkspaceUuid));
      if (selectedDashboard?.uuid === data.uuid) {
        dispatch(setSelectedDashboard(newDashboard));
      }
      showToast(
        "info",
        <IDHFormattedMessage id="ws_info" defaultMessage="Info" />,
        <IDHFormattedMessage
          id="ws_dashboard_renamed_successfully"
          defaultMessage="Dashboard renamed successfully"
        />,
      );
      onClose();
    } catch (err) {
      showErrorToast();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      className="rename-dashboard-modal"
      overlayClassName="rename-dashboard-modal__overlay"
      onClose={onClose}
      closeButtonText={
        <IDHFormattedMessage id="ws_cancel" defaultMessage="Cancel" />
      }
      displayCancelButton
      closeButtonVariant="white-with-light-grey-border"
      confirmButtonDisabled={name.trim().length < 3 || isNameTooLong}
      confirmButtonVariant="blue"
      confirmButtonText={
        <IDHFormattedMessage id="ws_save" defaultMessage="Save" />
      }
      confirmButtonLoading={loading}
      onConfirmClick={submit}
    >
      <ModalTitle>
        <IDHFormattedMessage
          id="ws_rename_dashboard"
          defaultMessage="Rename dashboard"
        />
      </ModalTitle>
      <CustomInput
        value={name}
        onChange={(e) => setName(e.target.value)}
        error={isNameTooLong}
        placeholder={translateMessage({
          intl,
          id: "ws_enter_name",
          defaultMessage: "Enter name",
        })}
      />
      {isNameTooLong && (
        <div className="rename-dashboard-modal__validation-message">
          <IDHFormattedMessage
            id="ws_dashboard_name_max_128_characters"
            defaultMessage="Dashboard name cannot exceed 128 characters"
          />
        </div>
      )}
    </Modal>
  );
};
